// Canonical source of truth for Manos's platform commission rates by
// subscription tier. Used server-side when creating Stripe Connect
// PaymentIntents to set application_fee_amount, AND consumed by the
// web/UI for display + comparison copy.
//
// This file lives at /config so both tsconfig.api.json and
// tsconfig.web.json can include it. Single home, both sides import via
// relative path -- avoids the cross-boundary drift that previously
// kept `api/lib/commissionRates.ts` and `web/lib/plans.ts` separately
// hardcoded with the same numbers.
//
// Per-app fee policy: Manos applies its commission to every digital
// transaction passing through the platform (Stripe Connect card +
// future digital rails). Cash and bank-transfer payments are never
// touched -- they don't pass through Stripe so there is no rail to
// fee. The seat covers Manos's platform value for those paths.
//
// Per-account Enterprise overrides are tracked in issue #114.

export type CommissionTier = "solo" | "team" | "enterprise";

/**
 * Default commission rates by tier. Solo is the entry rate; the
 * higher tiers reduce the rate as a reward for upgrading. Enterprise
 * is the ceiling -- actual Enterprise rates may be negotiated lower
 * on a per-account basis (issue #114).
 */
export const COMMISSION_TIER_DEFAULTS: Record<CommissionTier, number> = {
  solo: 0.01, // 1.0%
  team: 0.007, // 0.7%
  enterprise: 0.005, // 0.5% ceiling -- often negotiated below this
};

/**
 * Resolve the commission rate for a trader. Reads the per-account
 * `commissionRateOverride` if set (issue #114 will surface this in
 * Manos's admin tooling); otherwise falls back to the tier default.
 *
 * Falls back to Solo's rate if no plan is set so a misconfigured
 * trader never charges less than the entry-tier commission.
 */
export function resolveCommissionRate(opts: {
  plan?: string | null;
  override?: number | null;
}): number {
  if (typeof opts.override === "number" && opts.override >= 0) {
    return opts.override;
  }
  const tier = (opts.plan ?? "solo") as CommissionTier;
  return COMMISSION_TIER_DEFAULTS[tier] ?? COMMISSION_TIER_DEFAULTS.solo;
}

/**
 * Convenience alias used by the web/marketing surfaces (settings
 * subscription page, terms, public landing) that just want the
 * tier-default rate without any per-account override resolution.
 */
export function applicationFeeForPlan(plan: string | null | undefined): number {
  return resolveCommissionRate({ plan, override: null });
}
